import type { AbstractPartDefinition } from "@project/emerge-types";

/**
 * AbstractPartInteraction provides a framework for defining interactions between parts.
 * These interactions run after all buildsteps have completed.
 *
 * Each part interaction processes the complete list of parts and can modify them
 * based on their relationships.
 */
export abstract class AbstractPartInteraction<
  BlueprintType = unknown,
  SettingsType = unknown,
  PartType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> {
  public interactionName: string;

  constructor(interactionName: string) {
    this.interactionName = interactionName;
  }

  /**
   * Execute the interaction.
   * This method should identify relationships between parts and modify them accordingly.
   *
   * @param blueprint The blueprint used as reference
   * @param settings The configuration or settings
   * @param parts The complete array of parts that have been built
   * @returns The number of parts that were modified by this interaction
   */
  public abstract execute(
    blueprint: BlueprintType,
    settings: SettingsType,
    parts: PartType[],
  ): Promise<number>;

  /**
   * Helper method to find parts of a specific type
   *
   * @param parts Array of all parts
   * @param type The part type to filter for
   * @returns Array of parts matching the specified type
   */
  protected findPartsByType<T extends PartType>(
    parts: PartType[],
    type: string,
  ): T[] {
    return parts.filter((part) => part.type === type) as T[];
  }

  /**
   * Helper method to find parts that match a predicate
   *
   * @param parts Array of all parts
   * @param predicate Function that returns true for parts to include
   * @returns Array of parts matching the predicate
   */
  protected findParts<T extends PartType>(
    parts: PartType[],
    predicate: (part: PartType) => boolean,
  ): T[] {
    return parts.filter(predicate) as T[];
  }
}
